import { Injectable } from '@angular/core';

/**
 * Service for handling invoice and tax (IVA) calculations
 * Centralizes business logic for invoice-related computations
 */
@Injectable()
export class InvoiceCalculatorService {

    private readonly IVA_RATE = 0.16; // 16% tax rate

    constructor() { }

    /**
     * Calculate IVA based on factura requirement
     * @param subtotal - The subtotal amount
     * @param requiereFactura - Whether invoice is required
     * @returns The calculated IVA amount (16% if invoice required, 0 otherwise)
     */
    calculateIvaFromFacturaToggle(subtotal: number, requiereFactura: boolean): number {
        if (requiereFactura) {
            return subtotal * this.IVA_RATE;
        }
        return 0;
    }

    /**
     * Validate and adjust IVA amount to not exceed maximum allowed
     * @param ivaAmount - The IVA amount entered by user
     * @param subtotal - The subtotal amount
     * @returns Valid IVA amount (capped at maximum 16% of subtotal)
     */
    validateAndAdjustIva(ivaAmount: number, subtotal: number): number {
        // Ensure IVA is a valid positive number
        if (!ivaAmount || ivaAmount <= 0) {
            return 0;
        }

        // Calculate maximum allowed IVA (16% of subtotal)
        const maximoValorIva = subtotal * this.IVA_RATE;

        // Cap IVA at maximum allowed value
        if (ivaAmount <= maximoValorIva) {
            return ivaAmount;
        }

        return maximoValorIva;
    }

    /**
     * Calculate total from subtotal and IVA
     * @param subtotal - The subtotal amount
     * @param iva - The IVA amount
     * @returns The total amount (subtotal + IVA)
     */
    calculateTotal(subtotal: number, iva: number): number {
        return subtotal + iva;
    }

    /**
     * Update invoice calculations based on change type
     * @param confirmacion - The confirmation object to update
     * @param changeType - Type of change: 'factura', 'iva', or 'subtotal'
     * @param newValue - The new value for the changed field
     * @returns Updated confirmation object with recalculated values
     */
    updateInvoiceCalculations(
        confirmacion: any,
        changeType: 'factura' | 'iva' | 'subtotal',
        newValue: any
    ): any {
        const updated = { ...confirmacion };

        switch (changeType) {
            case 'factura':
                // User toggled factura requirement
                updated.iva = this.calculateIvaFromFacturaToggle(
                    updated.subtotal,
                    newValue
                );
                updated.total = this.calculateTotal(updated.subtotal, updated.iva);
                break;

            case 'iva':
                // User manually entered IVA amount
                updated.iva = this.validateAndAdjustIva(newValue, updated.subtotal);
                updated.total = this.calculateTotal(updated.subtotal, updated.iva);
                break;

            case 'subtotal':
                // Subtotal changed, recalculate total
                updated.total = this.calculateTotal(updated.subtotal, updated.iva);
                break;
        }

        return updated;
    }

    /**
     * Calculate subtotal with discount applied
     * @param subtotalSinDescuento - Original subtotal before discount
     * @param descuentoAmount - Discount amount to subtract
     * @returns New subtotal after discount
     */
    calculateSubtotalWithDiscount(
        subtotalSinDescuento: number,
        descuentoAmount: number
    ): number {
        return subtotalSinDescuento - descuentoAmount;
    }

    /**
     * Calculate IVA for a given subtotal and factura requirement
     * @param subtotal - The subtotal amount
     * @param requiereFactura - Whether invoice is required
     * @returns The calculated IVA amount
     */
    calculateIva(subtotal: number, requiereFactura: boolean): number {
        return requiereFactura ? subtotal * this.IVA_RATE : 0;
    }

    /**
     * Get the current IVA rate
     * @returns The IVA rate as a decimal (0.16 = 16%)
     */
    getIvaRate(): number {
        return this.IVA_RATE;
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/business-logic/invoice-calculator.service.ts